import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack, Typography, Box } from '@mui/material';

import { StepLayout } from '@hapvida/hapvida-core-components';

import { NEW_COLORS } from '@hapvida/hapvida-core-components/src/themes/colors/newColors';

import useDeviceBreakpoints from 'hooks/useDeviceBreakpoints';
import { Header } from 'templates/private/components';
import { Stepper } from 'components/Stepper';

import {
  PASSWORD_RECOVERY_STEPS_KEYS,
  PASSWORD_RECOVERY_STEPS_ORDER,
  PASSWORD_RECOVERY_STEPS_VALUES,
} from './constants/keys';
import { steps } from './passwordRecoverySteps';

function PasswordRecoveryStepsPage() {
  const navigateTo = useNavigate();
  const { isMobileOrTablet, isMobile, isTablet } = useDeviceBreakpoints();

  const routeParams = useParams<{ passwordDefinitionStep: string }>();

  const [currentStep, setCurrentStep] = useState(
    PASSWORD_RECOVERY_STEPS_VALUES[
      routeParams.passwordDefinitionStep ||
        PASSWORD_RECOVERY_STEPS_KEYS.PASSWORD_RECOVERY
    ],
  );

  useEffect(() => {
    if (routeParams.passwordDefinitionStep)
      setCurrentStep(
        PASSWORD_RECOVERY_STEPS_VALUES[routeParams.passwordDefinitionStep],
      );
  }, [routeParams]);

  const [stepList, setStepList] = useState([
    {
      label: 'Redefinição de senha',
      completed: true,
    },
    {
      label: 'Confirmação',
      completed: false,
    },
  ]);

  const handleStepChange = useCallback(
    (step: number) => {
      if (step === 0) return;

      setCurrentStep(step);
      navigateTo(
        `/recuperacao-de-senha/${PASSWORD_RECOVERY_STEPS_ORDER[step]}`,
      );
    },
    [navigateTo],
  );

  const handleStepUpdate = useCallback(
    (step: number) => {
      const updatedStepList: any = [...stepList];

      updatedStepList[step] = {
        ...stepList[step],
        completed: true,
      };

      setStepList(updatedStepList);
      setCurrentStep(step + 1);
      navigateTo(
        `/recuperacao-de-senha/${PASSWORD_RECOVERY_STEPS_ORDER[step + 1]}`,
      );
    },
    [navigateTo, stepList],
  );

  const getPaddingValue = () => {
    if (isTablet) return '105px';
    if (isMobile) return '34px';

    return undefined;
  };

  const renderContent = () => {
    return (
      <>
        <Stack
          marginBottom={4}
          gap={1}
          flexDirection="row"
          alignItems="flex-end"
          justifyContent="flex-end"
        >
          <Typography variant="text">Já tem cadastro?</Typography>
          <a href={process.env.REACT_APP_LOGIN_URL}>
            <Typography variant="link" color={NEW_COLORS.PRIMARY[500]}>
              Efetue o login
            </Typography>
          </a>
        </Stack>
        {isMobileOrTablet ? (
          <Box pt={3}>
            <Stepper
              steps={stepList.map(step => step.label)}
              activeStep={currentStep}
              onClickStep={handleStepChange}
            />
          </Box>
        ) : null}
        {steps[currentStep].step({
          onStepUpdate: handleStepUpdate,
        })}
      </>
    );
  };

  const isFirstStep = currentStep === 0;

  return isMobileOrTablet ? (
    <Header
      theme="light"
      mainContent={
        <Box mt={'124px'} px={getPaddingValue}>
          {renderContent()}
        </Box>
      }
      showMenu={false}
      showBackButton={!isFirstStep}
    />
  ) : (
    <StepLayout
      currentStep={currentStep}
      onStepChange={(step: number) => handleStepChange(step)}
      steps={stepList}
      hasHeader={false}
      hasFirstStep={false}
      showDrawer
    >
      {renderContent()}
    </StepLayout>
  );
}

export default PasswordRecoveryStepsPage;
